import { Call, EditRoad, HomeWork, LocationOn, Mail, Print, Public } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IAddress,
  IContact,
  useCompanyAddressesLazyQuery,
  useUpdateContactAddressMutation
} from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';
import CancelButton from '../../Common/Buttons/CancelButton';
import EditButton from '../../Common/Buttons/EditButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../Common/Fields/InputComponent';

interface IProps {
  contact: IContact;
}

const ContactOfficeTab: React.FC<IProps> = ({ contact }) => {
  const [updateContactAddress, updateContactAddressResult] = useUpdateContactAddressMutation();
  const [getCompanyAddresses, companyAddressesResult] = useCompanyAddressesLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [address, setAddress] = useState(contact.address);
  const [addressesOptions, setAddressesOption] = useState([]);
  const [editable, setEditable] = useState(false);
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Contact);

  useEffect(() => {
    if (companyAddressesResult.data && companyAddressesResult.data.result) {
      setAddressesOption(companyAddressesResult.data.result as never[]);
    }
  }, [companyAddressesResult.data]);

  useEffect(() => {
    if (updateContactAddressResult.data && updateContactAddressResult.data.result) {
      setEditable(false);
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: `Successfully updated ${updateContactAddressResult.data.result.fullName} office`,
          severity: 'success'
        }
      });
    }
  }, [updateContactAddressResult.data]);

  const handleAddressChanged = (_: unknown, newValue: IAddress | null) => {
    setAddress(newValue);
  };

  const handleEditClicked = () => {
    getCompanyAddresses({ variables: { company_id: parseInt(contact.company.id) } });
    setEditable(true);
  };

  const handleCancel = () => {
    setAddress(contact.address);
    setEditable(false);
  };

  const handleSave = () => {
    if (contact.address?.id !== address?.id) {
      updateContactAddress({
        variables: {
          id: parseInt(contact.id),
          company_id: parseInt(contact.company.id),
          address_id: address ? parseInt(address.id) : null
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: e.message, severity: 'error', autoHide: 6000 }
        })
      );
    } else {
      setEditable(false);
    }
  };

  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-general-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '60px'
        }}
      >
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: '50px' }}>
          <InputComponent
            id="contact-office-address-name"
            label="Co.address Name"
            value={address?.name || ''}
            disabled={true}
            startAdornment={
              <HomeWork
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
          <AutocompleteComponent
            id="contact-office-address"
            label="Address"
            options={addressesOptions}
            getOptionLabel={(option: IAddress) => option.address_name as string}
            disabled={!editable || updateContactAddressResult.loading}
            values={address}
            onChange={handleAddressChanged}
            startAdornment={
              <LocationOn
                sx={{
                  marginRight: '12px',
                  color: !editable || updateContactAddressResult.loading ? Colors.gray : Colors.main
                }}
              />
            }
          />
          <InputComponent
            id="contact-office-street"
            label="Street"
            value={address?.street || ''}
            disabled={true}
            startAdornment={
              <EditRoad
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="contact-office-country"
            label="Country"
            value={address?.country.name || ''}
            disabled={true}
            startAdornment={
              <Public
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
        </Box>
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: '50px' }}>
          <InputComponent
            id="contact-office-phone"
            label="Phone (direct)"
            value={address?.phone || ''}
            disabled={true}
            startAdornment={
              <Call
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="contact-office-fax"
            label="Fax (direct)"
            value={address?.fax || ''}
            disabled={true}
            startAdornment={
              <Print
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="contact-office-email"
            label="Email (general)"
            value={address?.email || ''}
            disabled={true}
            startAdornment={
              <Mail
                sx={{
                  marginRight: '12px',
                  color: Colors.gray
                }}
              />
            }
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '60px'
            }}
          >
            <InputComponent
              id="contact-office-city"
              label="City"
              value={address?.city || ''}
              disabled={true}
            />
            <InputComponent
              id="contact-office-zip"
              label="ZIP Code"
              value={address?.zip || ''}
              disabled={true}
            />
          </Box>
        </Box>
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '160px' }}>
          <CancelButton
            id="contact-office-cancel-button"
            disabled={!editable || updateContactAddressResult.loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="contact-office-save-button"
            disabled={!editable || updateContactAddressResult.loading}
            onClick={handleSave}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default ContactOfficeTab;
